import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [formData, setFormData] = useState({
    account_number: '',
    account_name: '',
    account_type: '',
    account_category: '',
    account_subcategory: '',
    description: '',
    normal_balance: 'Debit',
    status: 'active'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [accountsPerPage] = useState(20);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [retrieving, setRetrieving] = useState(false);

  // Dropdown states
  const [accountTypeDropdownOpen, setAccountTypeDropdownOpen] = useState(false);
  const [normalBalanceDropdownOpen, setNormalBalanceDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [filterAccountTypeDropdownOpen, setFilterAccountTypeDropdownOpen] = useState(false);

  // Account type options
  const accountTypes = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];
  const normalBalances = ['Debit', 'Credit'];
  const statuses = ['active', 'inactive'];

  useEffect(() => {
    fetchAccounts();
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations`);
      // Get all active integrations that support chart of accounts retrieval
      const supportedIntegrations = response.data.filter(
        integration => 
          integration.status === 'active' && 
          (integration.integration_type === 'quickbooks_online' || integration.integration_type === 'procore')
      );
      setIntegrations(supportedIntegrations);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  const handleRetrieveAccounts = async () => {
    if (!selectedIntegration) {
      alert('Please select an integration');
      return;
    }

    setRetrieving(true);
    try {
      let response;
      
      if (selectedIntegration.integration_type === 'quickbooks_online') {
        // Use QBO retrieval endpoint
        response = await axios.post(`${API_BASE_URL}/api/chartofaccounts/retrieve-from-qbo`, {
          integration_vuid: selectedIntegration.vuid
        });
      } else if (selectedIntegration.integration_type === 'procore') {
        // Future: Add Procore retrieval endpoint
        alert('Procore account retrieval is not yet implemented');
        setRetrieving(false);
        return;
      } else {
        alert('This integration type does not support account retrieval');
        setRetrieving(false);
        return;
      }

      if (response && response.data.success) {
        const integrationName = selectedIntegration.integration_name;
        alert(
          `✅ Successfully retrieved accounts from ${integrationName}!\n\n` +
          `Imported: ${response.data.imported_count}\n` +
          `Updated: ${response.data.updated_count}\n` +
          `Skipped: ${response.data.skipped_count}\n` +
          `Total Found: ${response.data.total_found}`
        );
        // Refresh the accounts list
        await fetchAccounts();
        setShowRetrieveModal(false);
        setSelectedIntegration(null);
      } else if (response) {
        alert(`Error: ${response.data.error || 'Failed to retrieve accounts'}`);
      }
    } catch (error) {
      console.error('Error retrieving accounts:', error);
      alert(error.response?.data?.error || 'Error retrieving accounts from integration');
    } finally {
      setRetrieving(false);
    }
  };

  useEffect(() => {
    filterAndPaginateAccounts();
  }, [accounts, searchTerm, accountTypeFilter]);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setAccountTypeDropdownOpen(false);
        setNormalBalanceDropdownOpen(false);
        setStatusDropdownOpen(false);
        setFilterAccountTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/chartofaccounts`);
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndPaginateAccounts = () => {
    let filtered = accounts;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(account =>
        account.account_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.account_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply account type filter
    if (accountTypeFilter !== 'all') {
      filtered = filtered.filter(account => account.account_type === accountTypeFilter);
    }

    setFilteredAccounts(filtered);
    setCurrentPage(1);
  };

  const getCurrentAccounts = () => {
    const indexOfLastAccount = currentPage * accountsPerPage;
    const indexOfFirstAccount = indexOfLastAccount - accountsPerPage;
    return filteredAccounts.slice(indexOfFirstAccount, indexOfLastAccount);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleFilterChange = (filterType, value) => {
    if (filterType === 'accountType') {
      setAccountTypeFilter(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const newErrors = {};
    if (!formData.account_number.trim()) {
      newErrors.account_number = 'Account number is required';
    }
    if (!formData.account_name.trim()) {
      newErrors.account_name = 'Account name is required';
    }
    if (!formData.account_type) {
      newErrors.account_type = 'Account type is required';
    }
    if (!formData.normal_balance) {
      newErrors.normal_balance = 'Normal balance is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      const url = editingAccount 
        ? `${API_BASE_URL}/api/chartofaccounts/${editingAccount.vuid}`
        : `${API_BASE_URL}/api/chartofaccounts`;
      
      const response = editingAccount
        ? await axios.put(url, formData)
        : await axios.post(url, formData);

      if (editingAccount) {
        setAccounts(accounts.map(acc => 
          acc.vuid === editingAccount.vuid ? response.data : acc
        ));
      } else {
        setAccounts([...accounts, response.data]);
      }
      
      handleCancelEdit();
      setErrors({});
    } catch (error) {
      setErrors({ submit: error.response?.data?.error || 'An error occurred while saving the account' });
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setShowCreateForm(false);
    setFormData({
      account_number: account.account_number,
      account_name: account.account_name,
      account_type: account.account_type,
      account_category: account.account_category || '',
      account_subcategory: account.account_subcategory || '',
      description: account.description || '',
      normal_balance: account.normal_balance,
      status: account.status
    });
  };

  const handleCancelEdit = () => {
    setEditingAccount(null);
    setShowCreateForm(false);
    setFormData({
      account_number: '',
      account_name: '',
      account_type: '',
      account_category: '',
      account_subcategory: '',
      description: '',
      normal_balance: 'Debit',
      status: 'active'
    });
    setErrors({});
  };

  const handleShowCreateForm = () => {
    setShowCreateForm(true);
    setEditingAccount(null);
    setFormData({
      account_number: '',
      account_name: '',
      account_type: '',
      account_category: '',
      account_subcategory: '',
      description: '',
      normal_balance: 'Debit',
      status: 'active'
    });
    setErrors({});
  };

  const handleDelete = async (vuid) => {
    if (!window.confirm('Are you sure you want to delete this account?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/chartofaccounts/${vuid}`);
      setAccounts(accounts.filter(a => a.vuid !== vuid));
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete account');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl font-semibold text-gray-700">Loading chart of accounts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">
              Chart of Accounts
            </h1>
            <p className="mt-2 text-gray-600">
              Manage construction company financial accounts and categories
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={showCreateForm ? handleCancelEdit : handleShowCreateForm}
              className="btn-claret"
            >
              {showCreateForm ? 'Cancel' : '+ Create New Account'}
            </button>
            <button
              onClick={() => setShowRetrieveModal(true)}
              className="btn-claret"
            >
              Retrieve Accounts
            </button>
          </div>
        </div>

        {/* Create/Edit Form */}
        {(showCreateForm || editingAccount) && (
          <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              {editingAccount ? 'Edit Account' : 'Create New Account'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 font-medium">{errors.submit}</p>
                </div>
              )}
              
              {/* Account Number and Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="account_number" className="block text-lg font-semibold text-gray-900 mb-2">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    id="account_number"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.account_number ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., 1000, 1010"
                  />
                  {errors.account_number && (
                    <p className="text-red-600 font-medium mt-2">{errors.account_number}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="account_name" className="block text-lg font-semibold text-gray-900 mb-2">
                    Account Name *
                  </label>
                  <input
                    type="text"
                    id="account_name"
                    name="account_name"
                    value={formData.account_name}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.account_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Cash, Accounts Receivable"
                  />
                  {errors.account_name && (
                    <p className="text-red-600 font-medium mt-2">{errors.account_name}</p>
                  )}
                </div>
              </div>

              {/* Account Type and Normal Balance */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="account_type" className="block text-lg font-semibold text-gray-900 mb-2">
                    Account Type *
                  </label>
                  <div className="relative dropdown-container">
                    <button
                      type="button"
                      onClick={() => setAccountTypeDropdownOpen(!accountTypeDropdownOpen)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all text-left bg-white"
                    >
                      {formData.account_type || 'Select Type'}
                      <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {accountTypeDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                        <div className="py-1">
                          {accountTypes.map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, account_type: type }));
                                setAccountTypeDropdownOpen(false);
                              }}
                              className="w-full px-4 py-3 text-left text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.account_type && (
                    <p className="text-red-600 font-medium mt-2">{errors.account_type}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="normal_balance" className="block text-lg font-semibold text-gray-900 mb-2">
                    Normal Balance *
                  </label>
                  <div className="relative dropdown-container">
                    <button
                      type="button"
                      onClick={() => setNormalBalanceDropdownOpen(!normalBalanceDropdownOpen)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all text-left bg-white"
                    >
                      {formData.normal_balance || 'Select Balance'}
                      <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {normalBalanceDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                        <div className="py-1">
                          {normalBalances.map((balance) => (
                            <button
                              key={balance}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, normal_balance: balance }));
                                setNormalBalanceDropdownOpen(false);
                              }}
                              className="w-full px-4 py-3 text-left text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                            >
                              {balance}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.normal_balance && (
                    <p className="text-red-600 font-medium mt-2">{errors.normal_balance}</p>
                  )}
                </div>
              </div>

              {/* Account Category and Subcategory */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="account_category" className="block text-lg font-semibold text-gray-900 mb-2">
                    Account Category
                  </label>
                  <input
                    type="text"
                    id="account_category"
                    name="account_category"
                    value={formData.account_category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    placeholder="e.g., Current Assets, Fixed Assets"
                  />
                </div>

                <div>
                  <label htmlFor="account_subcategory" className="block text-lg font-semibold text-gray-900 mb-2">
                    Account Subcategory
                  </label>
                  <input
                    type="text"
                    id="account_subcategory"
                    name="account_subcategory"
                    value={formData.account_subcategory}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    placeholder="e.g., Cash and Cash Equivalents"
                  />
                </div>
              </div>

              {/* Description and Status */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="description" className="block text-lg font-semibold text-gray-900 mb-2">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                    placeholder="Detailed description of the account"
                  />
                </div>

                <div>
                  <label htmlFor="status" className="block text-lg font-semibold text-gray-900 mb-2">
                    Status
                  </label>
                  <div className="relative dropdown-container">
                    <button
                      type="button"
                      onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all text-left bg-white"
                    >
                      {formData.status === 'active' ? 'Active' : 'Inactive'}
                      <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    
                    {statusDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                        <div className="py-1">
                          {statuses.map((status) => (
                            <button
                              key={status}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, status }));
                                setStatusDropdownOpen(false);
                              }}
                              className="w-full px-4 py-3 text-left text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                            >
                              {status === 'active' ? 'Active' : 'Inactive'}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center pt-4">
                <button
                  type="submit"
                  className="btn-claret"
                  >
                  {editingAccount ? 'Update Account' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="search" className="block text-sm font-semibold text-gray-700 mb-2">
                Search Accounts
              </label>
              <input
                type="text"
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by account number, name, or description..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
              />
            </div>

            <div>
              <label htmlFor="accountTypeFilter" className="block text-sm font-semibold text-gray-700 mb-2">
                Filter by Account Type
              </label>
              <div className="relative dropdown-container">
                <button
                  type="button"
                  onClick={() => setFilterAccountTypeDropdownOpen(!filterAccountTypeDropdownOpen)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all text-left bg-white"
                >
                  {accountTypeFilter === 'all' ? 'All Account Types' : accountTypeFilter}
                  <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {filterAccountTypeDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                    <div className="py-1">
                      <button
                        type="button"
                        onClick={() => {
                          handleFilterChange('accountType', 'all');
                          setFilterAccountTypeDropdownOpen(false);
                        }}
                        className="w-full px-4 py-3 text-left text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                      >
                        All Account Types
                      </button>
                      {accountTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => {
                            handleFilterChange('accountType', type);
                            setFilterAccountTypeDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 text-left text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Accounts List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Chart of Accounts ({filteredAccounts.length} accounts)
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Normal Balance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {getCurrentAccounts().map((account) => (
                  <tr key={account.vuid} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {account.account_number}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {account.account_name}
                        </div>
                        {account.description && (
                          <div className="text-sm text-gray-500">
                            {account.description}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        account.account_type === 'Asset' ? 'bg-blue-100 text-blue-800' :
                        account.account_type === 'Liability' ? 'bg-red-100 text-red-800' :
                        account.account_type === 'Equity' ? 'bg-green-100 text-green-800' :
                        account.account_type === 'Revenue' ? 'bg-purple-100 text-purple-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {account.account_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.account_category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        account.normal_balance === 'Debit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {account.normal_balance}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        account.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {account.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(account)}
                        className="text-vermillion-600 hover:text-vermillion-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(account.vuid)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {filteredAccounts.length > accountsPerPage && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.ceil(filteredAccounts.length / accountsPerPage) }, (_, i) => i + 1).map((number) => (
                <button
                  key={number}
                  onClick={() => paginate(number)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === number
                      ? 'bg-vermillion-600 text-white'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {number}
                </button>
              ))}
              
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === Math.ceil(filteredAccounts.length / accountsPerPage)}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}

        {/* Retrieve from QBO Modal */}
        {showRetrieveModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Retrieve Chart of Accounts
                  </h3>
                  <button
                    onClick={() => {
                      setShowRetrieveModal(false);
                      setSelectedIntegration(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Integration *
                  </label>
                  {integrations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="text-lg mb-2">No integrations found</p>
                      <p className="text-sm">Please create an integration (QuickBooks Online or Procore) first.</p>
                    </div>
                  ) : (
                    <select
                      value={selectedIntegration?.vuid || ''}
                      onChange={(e) => {
                        const integration = integrations.find(i => i.vuid === e.target.value);
                        setSelectedIntegration(integration);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Select Integration --</option>
                      {integrations.map(integration => (
                        <option key={integration.vuid} value={integration.vuid}>
                          {integration.integration_name} ({integration.integration_type === 'quickbooks_online' ? 'QuickBooks Online' : integration.integration_type})
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {selectedIntegration && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      {selectedIntegration.integration_type === 'quickbooks_online' ? (
                        <>
                          <strong>QuickBooks Online:</strong> This will retrieve all active accounts from QuickBooks Online and import them into your Chart of Accounts.
                          Accounts with the same account number will be updated if they already exist.
                        </>
                      ) : selectedIntegration.integration_type === 'procore' ? (
                        <>
                          <strong>Procore:</strong> Account retrieval from Procore is not yet implemented.
                        </>
                      ) : (
                        <>
                          <strong>Note:</strong> This integration type does not support account retrieval.
                        </>
                      )}
                    </p>
                  </div>
                )}

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowRetrieveModal(false);
                      setSelectedIntegration(null);
                    }}
                    className="btn-claret"
                    disabled={retrieving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRetrieveAccounts}
                    disabled={!selectedIntegration || retrieving || (selectedIntegration && selectedIntegration.integration_type !== 'quickbooks_online')}
                    className="btn-claret"
                    >
                    {retrieving ? 'Retrieving...' : 'Retrieve Accounts'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartOfAccounts;
